import * as React from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";

import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { IProductSubstitution } from "src/modules/substitutions/api/substitutions.api";
import { ProductStatus } from "src/core/products/api/products.api";
import useOrder from "src/core/orders/hooks/useOrder";

import { CompactProductCard } from "./CompactProductCard";
import {
  ProductCardWrapper,
  IStandardProductCardProps,
  StandardProductCard
} from "./StandardProductCard";

export interface IProductCardWithSubstitutionProps extends IStandardProductCardProps {
  substitution: IProductSubstitution;
  view: "grid" | "compact";
}

export const ProductCardWithSubstitution: React.ReactFCWithChildren<IProductCardWithSubstitutionProps> = ({
  product: prod,
  substitution,
  productImage,
  className,
  installedModules,
  currentRole,
  view
}) => {
  const product = _.omit(prod, "packSizes");
  const [ showAlternative, setShowAlternative ] = React.useState<boolean | undefined>(undefined);
  const [ { currentOrder } ] = useOrder();

  const hasOrderQuantity = React.useMemo(() => {
    if (currentOrder.data && currentOrder.mode === "edit") {
      return !!_.find(currentOrder.data.orderLines, ol => ol.productId === product.id && ol.quantity >= 1);
    }

    return false;
  }, [
    currentOrder.data,
    currentOrder.mode,
    product.id
  ]);

  React.useEffect(() => {
    if (showAlternative === undefined) {
      setShowAlternative(hasOrderQuantity);
    }
  }, [ hasOrderQuantity, showAlternative ]);

  if (substitution.replacementProduct.currentStatus?.status !== ProductStatus.Active) {
    if (view === "grid") {
      return (
        <StandardProductCard
          product={product}
          productImage={productImage}
          className={className}
          currentRole={currentRole}
          installedModules={installedModules}
          unavailable={substitution.message}
        />
      );
    } else {
      return (
        <CompactProductCard
          product={product}
          productImage={productImage}
          className={className}
          currentRole={currentRole}
          installedModules={installedModules}
          unavailable={substitution.message}
        />
      );
    }
  }

  return (
    <Wrapper
      direction={view === "grid" ? "vertical" : "horizontal"}
      className={`view-${view} ${className}`}
    >

      <SubstitutionMessage
        className={`${showAlternative ? "active" : "inactive"}`}
        onClick={() => setShowAlternative(!showAlternative)}
      >
        <Icon
          icon={Icons.SUBSTITUTION}
          $fillColor="midGrey"
          className="substitution-icon"
        />

        <SubstituteText weight="bold">
          {showAlternative ? "Show original product" : "Show alternative product"}
        </SubstituteText>
      </SubstitutionMessage>

      {view === "grid" ? (
        <>
          <StandardProductCard
            product={product}
            productImage={productImage}
            className={`original-item ${showAlternative ? "hidden" : ""} product-card`}
            currentRole={currentRole}
            installedModules={installedModules}
            unavailable={substitution.message}
          />

          <StandardProductCard
            className={`replacement-item ${!showAlternative ? "hidden" : ""} product-card`}
            product={substitution.replacementProduct}
            productImage={productImage}
            currentRole={currentRole}
            installedModules={installedModules}
            originalProduct={product}
          />
        </>
      ) : (
        <>
          <CompactProductCard
            product={product}
            productImage={productImage}
            className={`original-item ${showAlternative ? "hidden" : ""} compact product-card`}
            currentRole={currentRole}
            installedModules={installedModules}
            unavailable={substitution.message}
          />

          <CompactProductCard
            className={`replacement-item ${!showAlternative ? "hidden" : ""} compact product-card`}
            product={substitution.replacementProduct}
            productImage={productImage}
            currentRole={currentRole}
            installedModules={installedModules}
            originalProduct={product}
          />
        </>
      )}

    </Wrapper>
  );
};

const Wrapper = styled(ProductCardWrapper)`
  ${({ theme }) => css`
    display: flex;
    height: 100%;
    background-color: ${theme.colors.white};
    position: relative;

    &.view-grid {
      align-items: stretch;
    };

    &.view-compact {
      width: 100%;
      max-width: none;

      ${SubstitutionMessage} {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100px;
        min-width: 100px;

        span {
          white-space: break-spaces;
        }
      }

      .compact {
        width: 100%;
        display: flex;
        justify-content: space-between;

        .product-unavailable {
          top: 0;
          right: 150px;
          top: 50%;
          transform: translateY(-50%);
        }

        .product-card-actions {
          flex-wrap: wrap;
        }
        
      }
    }

    .original-item {
      &.hidden {
        display: none;
      }
    }

    .replacement-item {
       &.hidden {
        display: none;
      }
    }
  `}
`;

const SubstitutionMessage = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex: 1;
    background-color: ${theme.colors.lightGrey};
    height: 25px;

    &.active {
      background: ${theme.colors.primaryDark};

      span {
        color: ${theme.colors.white};
      }

      svg {
        fill: ${theme.colors.white}!important;
      }
    }

    &:hover {
      cursor: pointer;
      opacity: 0.8;
    }
  `}
`;

const SubstituteText = styled(Text)`
  margin-left: 1rem;
`;
